// Facet filter popover: sections of checkbox facets with counts (Section, Group, Modality, Flag…), an optional hint on a section's title shown as a tooltip, an optional badge after an option's label, long labels cut short with the full label on hover, active count on the trigger and Clear all.
import { InfoIcon, ListFilterIcon } from "lucide-react";
import { useId, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";

export interface FacetOption {
  value: string;
  label: string;
  count?: number;
  badge?: ReactNode;
}

export interface FacetSection {
  key: string;
  title: string;
  hint?: string;
  options: readonly FacetOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
}

interface FacetFilterProps {
  sections: readonly FacetSection[];
  label?: string;
  onClearAll?: () => void;
  className?: string;
}

function FacetSectionList({ section }: { section: FacetSection }) {
  const baseId = useId();
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-1 p-0 text-label font-medium tracking-caps text-muted-foreground uppercase">
        {section.hint === undefined ? (
          section.title
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-xs border-0 bg-transparent p-0 uppercase hover:text-foreground"
              >
                {section.title}
                <InfoIcon aria-hidden className="size-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{section.hint}</TooltipContent>
          </Tooltip>
        )}
      </legend>
      {section.options.length === 0 ? (
        <p className="m-0 py-1 text-xs text-muted-foreground">No values</p>
      ) : null}
      <ul className="m-0 flex list-none flex-col p-0">
        {section.options.map((option) => {
          const id = `${baseId}-${option.value}`;
          const checked = section.selected.includes(option.value);
          return (
            <li key={option.value} className="flex items-center gap-2 rounded-sm px-1 py-1 hover:bg-accent">
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(next) => {
                  section.onChange(
                    next === true
                      ? [...section.selected, option.value]
                      : section.selected.filter((value) => value !== option.value),
                  );
                }}
              />
              <label
                htmlFor={id}
                title={option.label}
                className="min-w-0 flex-1 cursor-pointer truncate text-ui"
              >
                {option.label}
              </label>
              {option.badge}
              {option.count === undefined ? null : (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatNumber(option.count)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

export function FacetFilter({ sections, label = "Filter", onClearAll, className }: FacetFilterProps) {
  const active = sections.reduce((count, section) => count + section.selected.length, 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(className)}
          aria-label={active > 0 ? `${label} (${active} active)` : label}
        >
          <ListFilterIcon aria-hidden />
          {label}
          {active > 0 ? <Badge variant="count">{active}</Badge> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="flex max-h-[min(60vh,520px)] pane-scroll flex-col gap-3.5 p-3">
          {sections.map((section) => (
            <FacetSectionList key={section.key} section={section} />
          ))}
        </div>
        {onClearAll ? (
          <div className="flex justify-end border-t bg-subtle px-3 py-2">
            <Button variant="ghost" size="sm" disabled={active === 0} onClick={onClearAll}>
              Clear all
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
